import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import axios from "axios";
import { addToCart } from "../redux/cartSlice";
import { addProduct, removeProduct } from "../redux/wishSlice";
import fetcher from '../utils/fetcher';
import useSWR from 'swr';
import ProductPageSkeleton from "../components/ProductPageSkeleton";
import RelatedSkeleton from "../components/RelatedSkeleton";
import { toast } from "react-toastify";
import styles from "../styles/productPage.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs, Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/autoplay";
import 'swiper/css/navigation';
import ProductImage from "../components/ProductImage";
import SpecialOrderModal from "../components/specialOrderModal";
import mediaUrl from "../utils/mediaUrl";
import { BsInfoCircle, BsCartCheck, BsLightningChargeFill } from 'react-icons/bs';
import { ImSpinner2 } from 'react-icons/im';
import { FaAward, FaHandshake, FaShippingFast, FaPlay, FaStar, FaRegTrashAlt } from 'react-icons/fa';
import { MdVerifiedUser, MdViewInAr, MdAutoAwesome } from 'react-icons/md';
import { FiEdit, FiHeart, FiChevronDown, FiChevronLeft, FiChevronRight, FiCamera, FiX, FiLayers } from 'react-icons/fi';
import explore from '../assets/explore.webp';
import ArIcon from '../components/ArIcon';
import DEFAULT_FAQS from '../data/defaultFaqs';

const RATING_LABELS = { 1: "سيء", 2: "مقبول", 3: "جيد", 4: "جيد جداً", 5: "ممتاز" };

const ProductPage = () => {
  const { id } = useParams();
  const [sortedDims, setSortedDims] = useState([]);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [lowestPrice, setLowestPrice] = useState(0);
  const [displayPrice, setDisplayPrice] = useState(0);
  const [material, setMaterial] = useState("");
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const swiperRef = useRef(null);
  const reviewsRef = useRef(null);
  // Marker right after the description + dimensions block: the mobile buy bar only
  // appears once the visitor has scrolled past it.
  const barTriggerRef = useRef(null);
  // Description: clamped to 2 lines with "عرض المزيد" when it's longer
  const descRef = useRef(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descOverflows, setDescOverflows] = useState(false);
  const [showMobileBar, setShowMobileBar] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const items = useSelector((state) => state.cart.items);
  const wishlist = useSelector((state) => state.wishlist.wishlist);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  // FAQ managed from the dashboard; the original questions show until any are added
  const { data: faqData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/faqs`, fetcher, {
    revalidateOnFocus: false,
    errorRetryCount: 1,
    dedupingInterval: 60000,
  });
  const faqs = Array.isArray(faqData) && faqData.length ? faqData : DEFAULT_FAQS;
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [reviews, setReviews] = useState([]);
  const [edit, setEdit] = useState(false);
  // Review photos (max 3): ones already saved on the user's review, new ones picked
  // in the form, and saved ones the user removed while editing
  const [revImages, setRevImages] = useState([]);       // [{ id, imageUrl }]
  const [revFiles, setRevFiles] = useState([]);         // [{ file, preview }]
  const [revRemovedIds, setRevRemovedIds] = useState([]);
  const [lightbox, setLightbox] = useState(null);       // { images: [url], index }
  const revInputRef = useRef(null);
  const [showAbout, setShowAbout] = useState(false);
  const [openSpecial, setOpenSpecial] = useState(false);

  const { data, error, isValidating } = useSWR(
    `${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      shouldRetryOnError: (err) => err.status !== 304,
      errorRetryCount: 2,
      dedupingInterval: 10000
    }
  );

  const isLoadingInitial = !data || isValidating;

  const product = data?.products[0] || null;

  useEffect(() => {
    if (product) {
      if (product.dimensions?.length > 0) {
        const sortedDims = [...product.dimensions].sort((a, b) => a.matte_price - b.matte_price);
        setSortedDims(sortedDims);
        setSelectedDimension(sortedDims[0]);
        setMaterial("matte");
        setDisplayPrice(sortedDims[0].matte_price);
        setLowestPrice(sortedDims[0].matte_price);
      }
    }
  }, [product]);

  const add = (product, lowestPrice) => {
    dispatch(addProduct({
      product: {
        product_id: Number(id),
        name: product.name,
        image_url: product.images[0].image_url
      },
      lowestPrice: lowestPrice
    }))
  };

  const remove = (id) => {
    dispatch(removeProduct({product_id: Number(id)}))
  };

  const isWished = wishlist.some(item => item.product.product_id === Number(id));

  const toggleWish = () => {
    if (isWished) {
      remove(id);
    } else {
      add(product, lowestPrice);
    }
  };

  const keywords = useMemo(() => {
    if (!product) return '';

    return [
      product.name,
      product.description
    ].filter(Boolean).join(', ');
  }, [product]);

  const structuredData = useMemo(() => {
    if (!product) return null;

    return {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "sku": product.code,
      "brand": { "@type": "Brand", "name": "Fazakir" }
    }
  }, [product]);

  useEffect(() => {
    setThumbsSwiper(null);
  }, [id]);

  const handleVideoPlay = () => {
    // Manually stop Swiper autoplay
    if (swiperRef.current?.swiper) {
      const swiper = swiperRef.current.swiper;

      if (swiper.autoplay?.running) {
        swiper.autoplay.stop();
      }
    }
  };

  const handleVideoPause = () => {
    // Restart Swiper autoplay
    if (swiperRef.current?.swiper) {
      const swiper = swiperRef.current.swiper;

      if (!swiper.autoplay.running) {
        swiper.autoplay.start();
      }
    }
  };

  const fetchReviews = useCallback(async () => {
    try {
      // Signed-in users are identified by the auth header, so their own review comes back as userRev
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/reviews/${id}`);
      setReviews(response.data.data);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    }
    // user?.id: refetch when signing in/out so "your review" (userRev) updates
  }, [id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    if (reviews.userRev) {
      setStars(reviews.userRev.stars)
      setText(reviews.userRev.text)
    } else {
      setStars(0)
      setText("")
      setEdit(false)
    }
    resetReviewPhotos(reviews.userRev);
  }, [reviews.userRev]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rating summary for the buy box
  const allReviews = useMemo(() => {
    const list = [...(reviews.reviews || [])];
    if (reviews.userRev) list.push(reviews.userRev);
    return list;
  }, [reviews]);

  const reviewCount = allReviews.length;
  const avgRating = reviewCount
    ? allReviews.reduce((sum, r) => sum + r.stars, 0) / reviewCount
    : 0;

  const [relatedData, setRelatedData] = useState(null);
  const [relatedError, setRelatedError] = useState(null);
  const [relatedLoading, setRelatedLoading] = useState(true);

  // Does the description need more than 2 lines? (measured while clamped)
  useEffect(() => {
    setDescExpanded(false);
    const el = descRef.current;
    if (!el) return;
    const measure = () => {
      if (el.classList.contains(styles.descriptionClamped)) {
        setDescOverflows(el.scrollHeight > el.clientHeight + 1);
      }
    };
    measure();
    // Re-check once the web font has loaded (it can change where lines wrap)
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [product?.description]);

  // Show the mobile buy bar only after the description + dimensions have been
  // scrolled past (the marker is above the top of the screen); hide it again when
  // the visitor scrolls back up to them.
  useEffect(() => {
    const el = barTriggerRef.current;
    if (!el) {
      setShowMobileBar(false);
      return;
    }
    if (!("IntersectionObserver" in window)) {
      setShowMobileBar(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      setShowMobileBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [product]);

  const fetchRelated = useCallback(async () => {
    if (!product?.categoryIds?.length || !id) return;

    setRelatedLoading(true);
    try {
      // Products sharing any of this product's categories
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/related/${id}`);
      setRelatedData(response.data);
      setRelatedError(null);
    } catch (error) {
      setRelatedError(error);
    } finally {
      setRelatedLoading(false);
    }
  }, [product?.categoryIds?.length, id]);

  useEffect(() => {
    fetchRelated();
  }, [fetchRelated]);

  const isLoadingRelated = relatedLoading;

  const relatedProducts = relatedData?.products || [];
  const lowestPrices = relatedData?.lowestPrices || [];

  const selectMaterial = (value) => {
    setMaterial(value);
    if (selectedDimension) {
      setDisplayPrice(value === "matte" ? selectedDimension.matte_price : selectedDimension.gloss_price);
    }
  };

  const validateSelection = (product) => {
    if (product.dimensions.length > 0 && !selectedDimension) {
      toast.error(<span className="toast">اختر المقاس اولاً</span>);
      return false;
    }

    if (!material) {
      toast.error(<span className="toast">اختر الخامة اولاً</span>);
      return false;
    }

    return true;
  };

  const buildCartItem = (product) => {
    const mainImg = product.images.find(img => img.is_main);
    return {id, name: product.name, image_url: mainImg.image_url, dimension: selectedDimension, material: material, price: displayPrice, quantity: 1, multi: Boolean(product.multi)};
  };

  const handleAddToCart = (product) => {
    if (!validateSelection(product)) return;

    if (items.length >= 30) {
      toast.error(<span className="toast">لقد وصلت للحد الأقصى للمنتجات فى السلة</span>);
      return;
    }

    if (items?.some(item => item.dimension.dim_id === selectedDimension.dim_id && item.material === material && item.id === id)) {
      toast.success(<span className="toast">المنتج موجود بالسلة بالفعل</span>);
      return;
    }

    try {
      dispatch(addToCart(buildCartItem(product)));

      toast.success(<span className="toast">تم إضافة المنتج إلى السلة بنجاح</span>);
    } catch (error) {
      toast.error(<span className="toast">لم نتمكن من إضافة المنتج إلى السلة. حاول مرة اخرى</span>);
    }
  };

  const handleBuyNow = (product) => {
    if (!validateSelection(product)) return;

    if (!user) {
      navigate("/login");
      return;
    }

    if (!user.name || !user.mobile || !user.street) {
      navigate("/account", { state: { fromCart: true } });
      return;
    }

    navigate("/checkout", { state: { fromCart: true, directBuy: buildCartItem(product) } });
  };

  // ── "اكتشف المزيد" cards: favourite, add to cart, buy now ──
  // A related card has no size chosen, so these use the product's cheapest size in
  // Matte — the "يبدأ من" price shown on the card.
  const [relatedBusy, setRelatedBusy] = useState(null); // `${productId}:${action}`

  const isRelatedWished = (rp) => wishlist.some((item) => item.product.product_id === rp.product_id);

  const toggleRelatedWish = (e, rp, price) => {
    e.stopPropagation();
    if (isRelatedWished(rp)) dispatch(removeProduct(rp));
    else dispatch(addProduct({ product: rp, lowestPrice: price }));
  };

  const buildRelatedCartItem = async (rp) => {
    const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/${rp.product_id}`);
    const details = res.data?.products?.[0];
    const cheapest = [...(details?.dimensions ?? [])].sort((a, b) => a.matte_price - b.matte_price)[0];
    if (!cheapest) return null;
    const mainImg = details.images?.find((img) => img.is_main) ?? details.images?.[0];
    return {
      id: String(rp.product_id), // same format as items added from a product page
      name: details.name,
      image_url: mainImg?.image_url ?? rp.image_url,
      dimension: cheapest,
      material: "matte",
      price: cheapest.matte_price,
      quantity: 1,
      multi: Boolean(details.multi),
    };
  };

  const relatedAddToCart = async (e, rp) => {
    e.stopPropagation();
    if (relatedBusy) return;
    if (items.length >= 30) {
      toast.error(<span className="toast">لقد وصلت للحد الأقصى للمنتجات فى السلة</span>);
      return;
    }
    setRelatedBusy(`${rp.product_id}:cart`);
    try {
      const item = await buildRelatedCartItem(rp);
      if (!item) {
        toast.error(<span className="toast">هذه اللوحة غير متاحة حالياً</span>);
        return;
      }
      if (items.some((it) => String(it.id) === item.id && it.dimension.dim_id === item.dimension.dim_id && it.material === item.material)) {
        toast.success(<span className="toast">المنتج موجود بالسلة بالفعل</span>);
        return;
      }
      dispatch(addToCart(item));
      toast.success(
        <span className="toast">
          تمت إضافة {item.name} ({item.dimension.width}×{item.dimension.height} سم - Matte) إلى السلة
        </span>
      );
    } catch {
      toast.error(<span className="toast">لم نتمكن من إضافة المنتج إلى السلة. حاول مرة اخرى</span>);
    } finally {
      setRelatedBusy(null);
    }
  };

  const relatedBuyNow = async (e, rp) => {
    e.stopPropagation();
    if (relatedBusy) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.name || !user.mobile || !user.street) {
      navigate("/account", { state: { fromCart: true } });
      return;
    }
    setRelatedBusy(`${rp.product_id}:buy`);
    try {
      const item = await buildRelatedCartItem(rp);
      if (!item) {
        toast.error(<span className="toast">هذه اللوحة غير متاحة حالياً</span>);
        return;
      }
      navigate("/checkout", { state: { fromCart: true, directBuy: item } });
    } catch {
      toast.error(<span className="toast">حدث خطأ. حاول مرة اخرى</span>);
    } finally {
      setRelatedBusy(null);
    }
  };

  const MAX_REVIEW_PHOTOS = 3;

  function resetReviewPhotos(userRev) {
    revFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    setRevImages(userRev?.images ?? []);
    setRevFiles([]);
    setRevRemovedIds([]);
  }

  const reviewPhotoCount = revImages.length + revFiles.length;

  const handleReviewPhotos = (e) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    const room = MAX_REVIEW_PHOTOS - reviewPhotoCount;
    if (picked.length > room) {
      toast.error(<span className="toast">يمكنك إضافة {MAX_REVIEW_PHOTOS} صور كحد أقصى</span>);
    }
    const ok = picked.slice(0, Math.max(room, 0)).filter((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 5 ميجابايت</span>);
        return false;
      }
      return true;
    });
    setRevFiles((prev) => [...prev, ...ok.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const removeSavedReviewPhoto = (photoId) => {
    setRevImages((prev) => prev.filter((img) => img.id !== photoId));
    setRevRemovedIds((prev) => [...prev, photoId]);
  };

  const removeNewReviewPhoto = (index) => {
    setRevFiles((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const openLightbox = (images, index) =>
    setLightbox({ images: images.map((img) => mediaUrl(img.imageUrl)), index });

  const buildReviewForm = () => {
    const fd = new FormData();
    fd.append("stars", stars);
    fd.append("text", text ?? "");
    revFiles.forEach((f) => fd.append("images", f.file));
    return fd;
  };

  const handleSend = async () => {
    if (stars < 1 || stars > 5) {
      toast.error(<span className="toast">اختر تقييماً من 1 إلى 5 نجوم</span>);
      return;
    }

    if (!user.name) {
      toast.error(<span className="toast">اضف اسمك فى صفحة حسابك حتى تتمكن من التقييم</span>);
      return;
    }

    if (edit) {
      try {
        const fd = buildReviewForm();
        if (revRemovedIds.length) fd.append("removeImageIds", revRemovedIds.join(","));
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/products/updateReview/${id}`, fd, { withCredentials: true });
        await fetchReviews();
        setEdit(false);
        toast.success(<span className="toast">تم تحديث التقييم بنجاح</span>);
      } catch (error) {
        toast.error(<span className="toast">لم نتمكن من تحديث التقييم. حاول مرة اخرى</span>);
        console.error("Error occurred in API call:", error);
      }
    } else {
      try {
        const fd = buildReviewForm();
        fd.append("name", user.name);
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/products/addReview/${id}`, fd, { withCredentials: true });
        await fetchReviews();
        toast.success(<span className="toast">تم إرسال التقييم بنجاح</span>);
      } catch (error) {
        toast.error(<span className="toast">لم نتمكن من إرسال التقييم. حاول مرة اخرى</span>);
        console.error("Error occurred in API call:", error);
      }
    }
  }

  const handleDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/products/deleteReview/${id}`, { withCredentials: true });
      setStars(0);
      setText("");
      await fetchReviews();
      toast.success(<span className="toast">تم حذف التقييم بنجاح</span>);
    } catch (error) {
      toast.error(<span className="toast">لم نتمكن من حذف التقييم. حاول مرة اخرى</span>);
      console.error("Error occurred in API call:", error);
    }
  };

  const scrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (error && error.status === 404) return <p className="warn">هذا المنتج غير موجود. ربما قمت بالضغط على رابط خاطىء أو قديم</p>;

  if (error && ![304, 404].includes(error.status)) {
    return <p className="warn">حدث خطأ اثناء تحميل بيانات المنتج. حاول مرة اخرى</p>
  };

  return (
    <div className={styles.page}>
      {isLoadingInitial ? (
        <ProductPageSkeleton count={1} />
      ) : (
        <>
          {product ? (
            <>
              <Helmet>
                <title>{product.name} | Fazakir</title>
                <meta name="description" content={product.description.substring(0, 160)} />
                <meta name="keywords" content={keywords} />

                {/* Open Graph */}
                <meta property="og:title" content={product.name} />
                <meta property="og:description" content={product.description} />
                <meta property="og:image" content={product.images[0]?.url} />
                <meta property="og:url" content={`https://fazakir.com/product/${id}`} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />

                {/* Structured Data */}
                <script type="application/ld+json">
                  {JSON.stringify(structuredData)}
                </script>
              </Helmet>

              {/* -------- Breadcrumb -------- */}
              <nav className={styles.breadcrumb}>
                <Link to="/">الرئيسية</Link>
                <FiChevronLeft />
                <Link to="/products">جميع اللوحات</Link>
                <FiChevronLeft />
                <span>{product.name}</span>
              </nav>

              <div className={styles.container}>
                {/* -------- Gallery -------- */}
                {product.images?.length > 0 && (
                  <div className={styles.swiperContainer}>
                    <Swiper
                      ref={swiperRef}
                      modules={[Thumbs, Autoplay]}
                      autoplay={{delay: 5000, disableOnInteraction: false}}
                      loop={product.images.length + (product.video ? 1 : 0) > 1}
                      thumbs={{ swiper: thumbsSwiper }}
                      className={styles.mainSwiper}
                    >
                      {product.images.map((img, idx) => (
                        <SwiperSlide key={idx}>
                          <ProductImage
                            url={img.image_url}
                            alt={`${product.name} ${idx + 1}`}
                            loading="lazy"
                            className={styles.mainImage}
                          />
                        </SwiperSlide>
                      ))}

                      {product.video &&
                        <SwiperSlide>
                          <video
                            src={mediaUrl(product.video)}
                            controls
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                            onEnded={handleVideoPause}
                            controlsList="nodownload noremoteplayback"
                            preload="metadata"
                            playsInline
                            webkit-playsinline="true"
                            className={`${styles.mainImage} ${styles.video}`}
                          >
                            <p>Video type is not supported in your browser</p>
                          </video>
                        </SwiperSlide>
                      }

                      <div className={styles.overlayTop}>
                        <button
                          className={styles.wishButton}
                          aria-label="أضف للمفضلة"
                          onClick={toggleWish}
                        >
                          <FiHeart style={{fill: isWished ? "#a8825c" : "none"}} />
                        </button>
                      </div>

                      <button className={styles.arButton} onClick={() => navigate(`/wall-preview?product=${id}`)}>
                        <ArIcon className={styles.arIcon} />
                        <span>اكتشفها على حائطك</span>
                      </button>
                    </Swiper>

                    <Swiper
                      onSwiper={setThumbsSwiper}
                      modules={[Thumbs]}
                      direction="horizontal"
                      slidesPerView={Math.min(product.images?.length + (product.video ? 1 : 0), 5)}
                      spaceBetween={10}
                      watchSlidesProgress
                      breakpoints={{
                        712: {
                          direction: "vertical",
                          // Fixed slot count keeps thumbs square-ish even with
                          // few images (otherwise they stretch into tall slivers).
                          slidesPerView: 5
                        }
                      }}
                      className={styles.thumbSwiper}
                    >
                      {product.images?.map((img, idx) => (
                        <SwiperSlide key={idx}>
                          <ProductImage
                            url={img.image_url}
                            alt={`${product.name} ${idx + 1}`}
                            loading="lazy"
                            className="thumbnail"
                          />
                        </SwiperSlide>
                      ))}

                      {product.video &&
                        <SwiperSlide>
                          <FaPlay className={styles.play} />
                          <video
                            src={mediaUrl(product.video)}
                            className="thumbnail"
                          >
                            <p>Video type is not supported in your browser</p>
                          </video>
                        </SwiperSlide>
                      }
                    </Swiper>
                  </div>
                )}

                {/* -------- Buy box -------- */}
                <div className={styles.details}>
                  <div className={styles.header}>
                    <h1 className={styles.name}>{product.name}</h1>
                    <span className={styles.codeChip}>كود {product.code}</span>
                    {Boolean(product.multi) && (
                      <span className={styles.setBadge}>
                        <FiLayers /> مجموعة لوحات متعددة
                      </span>
                    )}
                  </div>

                  {reviewCount > 0 && (
                    <button type="button" className={styles.ratingSummary} onClick={scrollToReviews}>
                      <span className={styles.ratingStars}>
                        {[...Array(5)].map((_, index) => (
                          <FaStar
                            key={index}
                            style={{fill: (index + 1) <= Math.round(avgRating) ? "#e7a934" : "#e4e0d8"}}
                          />
                        ))}
                      </span>
                      <span className={styles.ratingCount}>
                        {avgRating.toFixed(1)} ({reviewCount} {reviewCount === 1 ? "تقييم" : "تقييمات"})
                      </span>
                    </button>
                  )}

                  <p
                    ref={descRef}
                    className={`${styles.description} ${descExpanded ? "" : styles.descriptionClamped}`}
                  >
                    {product.description}
                  </p>
                  {(descOverflows || descExpanded) && (
                    <button
                      type="button"
                      className={styles.descToggle}
                      onClick={() => setDescExpanded((v) => !v)}
                      aria-expanded={descExpanded}
                    >
                      {descExpanded ? "عرض أقل" : "عرض المزيد"}
                    </button>
                  )}

                  {sortedDims.length > 0 && (
                    <div className={styles.optionBlock}>
                      <p className={styles.optionTitle}>
                        الأبعاد <span className={styles.optionHint}>العرض × الارتفاع سم</span>
                      </p>


                      <div className={styles.dimensions}>
                        {sortedDims.map(dim => (
                          <button
                            key={dim.dim_id}
                            onClick={() => {
                              setSelectedDimension(dim);
                              setDisplayPrice(material === "matte" ? dim.matte_price : dim.gloss_price);
                            }}
                            className={
                              selectedDimension?.width === dim.width && selectedDimension?.height === dim.height
                                ? `${styles.dimPill} ${styles.selected}`
                                : styles.dimPill
                            }
                          >
                            {dim.width} × {dim.height}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <span ref={barTriggerRef} className={styles.barTrigger} aria-hidden="true" />

                  <div className={styles.optionBlock}>
                    <p className={styles.optionTitle}>الخامة</p>

                    <div className={styles.materials}>
                      <button
                        type="button"
                        onClick={() => selectMaterial("matte")}
                        className={material === "matte" ? `${styles.materialCard} ${styles.selected}` : styles.materialCard}
                      >
                        <strong>Matte</strong>
                        <span>قماش كانفاس مطفي بدون لمعة (صيني)</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => selectMaterial("gloss")}
                        className={material === "gloss" ? `${styles.materialCard} ${styles.materialGold} ${styles.selected}` : `${styles.materialCard} ${styles.materialGold}`}
                      >
                        <strong>Luster</strong>
                        <span>طباعة متحفية مع لمعة خفيفة تضفي فخامة وسطوع للألوان — ضمان 25 سنة على الألوان (فرنسي)</span>
                      </button>
                    </div>
                  </div>

                  <div className={styles.priceBlock}>
                    <div className={styles.priceRow}>
                      <span className={styles.priceLabel}>السعر</span>
                      <span className={styles.priceValue}>{displayPrice} <small>شيكل</small></span>
                      {Boolean(product.multi) && (
                        <span className={styles.multiChip}>شامل جميع القطع</span>
                      )}
                    </div>

                    {/* Multi-panel set: the photos show its different pieces, one price covers all */}
                    {Boolean(product.multi) && (
                      <div className={styles.setNote}>
                        <span className={styles.setNoteIcon}><FiLayers /></span>
                        <div>
                          <strong>هذه مجموعة من عدة لوحات</strong>
                          <p>الصور توضح القطع المختلفة في المجموعة، والسعر المعروض يشمل جميع اللوحات معاً.</p>
                        </div>
                      </div>
                    )}

                    <p className={styles.tax}><BsInfoCircle /> السعر يشمل الضريبة</p>
                  </div>

                  <div className={styles.ctaRow}>
                    <button
                      className={styles.addtocart}
                      onClick={() => handleAddToCart(product)}
                    >
                      <BsCartCheck /> أضف إلى السلة
                    </button>

                    <button
                      className={styles.buyNow}
                      onClick={() => handleBuyNow(product)}
                    >
                      <BsLightningChargeFill /> اشترِ الآن
                    </button>

                    <button
                      type="button"
                      className={styles.wishSecondary}
                      aria-label="أضف للمفضلة"
                      onClick={toggleWish}
                    >
                      <FiHeart style={{fill: isWished ? "#a8825c" : "none"}} />
                    </button>
                  </div>

                  <div className={styles.trustRow}>
                    <span><MdVerifiedUser /> دفع آمن</span>
                    <span><FaAward /> ضمان على الجودة</span>
                    <span><FaShippingFast /> شحن سريع</span>
                  </div>
                </div>
              </div>

              {/* -------- Services promo -------- */}
              <div className={styles.servicesPromo}>
                <button type="button" className={styles.serviceCard} onClick={() => navigate(`/wall-preview?product=${id}`)}>
                  <span className={styles.serviceIcon}><MdViewInAr /></span>
                  <span className={styles.serviceText}>
                    <strong>شاهدها على حائطك</strong>
                    <span>جرب اللوحة على صورة حائطك قبل الشراء</span>
                  </span>
                  <FiChevronLeft className={styles.serviceArrow} />
                </button>

                <button type="button" className={styles.serviceCard} onClick={() => setOpenSpecial(true)}>
                  <span className={styles.serviceIcon}><MdAutoAwesome /></span>
                  <span className={styles.serviceText}>
                    <strong>صمم لوحتك الخاصة</strong>
                    <span>اطلب تصميماً مخصصاً بالحجم والألوان التى تناسبك</span>
                  </span>
                  <FiChevronLeft className={styles.serviceArrow} />
                </button>
              </div>

              {/* -------- Mobile sticky buy bar -------- */}
              <div className={`${styles.mobileBar} ${showMobileBar ? styles.mobileBarVisible : ""}`} aria-hidden={!showMobileBar}>
                <div className={styles.mobileBarPrice}>
                  <span>{product.multi ? "سعر المجموعة" : "السعر"}</span>
                  <strong>{displayPrice} ₪</strong>
                </div>
                <button onClick={() => handleAddToCart(product)}>
                  <BsCartCheck /> أضف إلى السلة
                </button>

                <button className={styles.buyNow} onClick={() => handleBuyNow(product)}>
                  <BsLightningChargeFill /> اشترِ الآن
                </button>
              </div>

              {/* -------- Reviews -------- */}
              {(user || reviews.reviews?.length > 0 || reviews.userRev) && (
                <section ref={reviewsRef} className={styles.section}>
                  <h2 className={styles.sectionTitle}>
                    التقييمات {reviewCount > 0 && <span className={styles.sectionCount}>({reviewCount})</span>}
                  </h2>

                  <div className={styles.reviewsLayout}>
                    <div className={styles.userReviewCol}>
                      {((user && !reviews.userRev) || (reviews.userRev && edit)) && (
                        <div className={styles.addReview}>
                          <p className={styles.addReviewTitle}>
                            {RATING_LABELS[hover || stars] || "قيّم هذه اللوحة"}
                          </p>

                          <div className={styles.starsRow}>
                            {[...Array(5)].map((_, index) => (
                              <FaStar
                                key={index}
                                className={styles.star}
                                onClick={() => {
                                  setStars(index + 1);
                                }}
                                onMouseEnter={() => setHover(index + 1)}
                                onMouseLeave={() => setHover(0)}
                                style={{fill: (index + 1) <= (hover || stars) ? "#e7a934" : "#e4e0d8"}}
                              />
                            ))}
                          </div>

                          <textarea
                            placeholder="اكتب تقييمك ..."
                            maxLength={255}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                          ></textarea>

                          {/* Review photos */}
                          <div className={styles.reviewPhotos}>
                            {revImages.map((img) => (
                              <div key={`s-${img.id}`} className={styles.reviewPhotoThumb}>
                                <img src={mediaUrl(img.imageUrl)} alt="" />
                                <button type="button" onClick={() => removeSavedReviewPhoto(img.id)} aria-label="حذف الصورة">
                                  <FiX />
                                </button>
                              </div>
                            ))}
                            {revFiles.map((f, i) => (
                              <div key={f.preview} className={styles.reviewPhotoThumb}>
                                <img src={f.preview} alt="" />
                                <button type="button" onClick={() => removeNewReviewPhoto(i)} aria-label="حذف الصورة">
                                  <FiX />
                                </button>
                              </div>
                            ))}
                            {reviewPhotoCount < MAX_REVIEW_PHOTOS && (
                              <button
                                type="button"
                                className={styles.reviewPhotoAdd}
                                onClick={() => revInputRef.current?.click()}
                              >
                                <FiCamera />
                                <span>أضف صورة</span>
                              </button>
                            )}
                            <input
                              ref={revInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              hidden
                              onChange={handleReviewPhotos}
                            />
                          </div>
                          <p className={styles.reviewPhotoHint}>يمكنك إضافة حتى {MAX_REVIEW_PHOTOS} صور للوحة بعد تعليقها</p>

                          <div className={styles.addReviewActions}>
                            <button className={styles.reviewSend} onClick={handleSend}>
                              {edit ? "تعديل" : "إرسال"}
                            </button>

                            {edit &&
                              <button
                                onClick={() => {
                                  setEdit(false);
                                  setStars(reviews.userRev.stars);
                                  setText(reviews.userRev.text);
                                  resetReviewPhotos(reviews.userRev);
                                }}
                                className={styles.reviewCancel}
                              >رجوع</button>
                            }
                          </div>
                        </div>
                      )}

                      {(reviews.userRev && !edit) && (
                        <div className={`${styles.reviewCard} ${styles.userRev}`}>
                          <div className={styles.reviewCardHead}>
                            <span className={styles.reviewName}>أنــت</span>

                            <div className={styles.userRevActions}>
                              <button onClick={() => setEdit(true)}>
                                <FiEdit /> تعديل
                              </button>

                              <button onClick={handleDelete} className={styles.deleteBtn} aria-label="حذف التقييم">
                                <FaRegTrashAlt />
                              </button>
                            </div>
                          </div>

                          <div className={styles.reviewStars}>
                            {[...Array(5)].map((_, index) => (
                              <FaStar
                                key={index}
                                style={{fill: (index + 1) <= reviews.userRev.stars ? "#e7a934" : "#e4e0d8"}}
                              />
                            ))}
                          </div>

                          {reviews.userRev.text && <p className={styles.reviewText}>{reviews.userRev.text}</p>}
                          {reviews.userRev.images?.length > 0 && (
                            <div className={styles.reviewCardPhotos}>
                              {reviews.userRev.images.map((img, i) => (
                                <button key={img.id} type="button" onClick={() => openLightbox(reviews.userRev.images, i)}>
                                  <img src={mediaUrl(img.imageUrl)} alt="صورة من العميل" loading="lazy" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {reviews.reviews?.length > 0 && (
                      <div className={styles.reviewsGrid}>
                        {reviews.reviews.map((r, idx) => (
                          <div key={idx} className={styles.reviewCard}>
                            <div className={styles.reviewCardHead}>
                              <span className={styles.reviewName}>{r.name}</span>
                            </div>

                            <div className={styles.reviewStars}>
                              {[...Array(5)].map((_, index) => (
                                <FaStar
                                  key={index}
                                  style={{fill: (index + 1) <= r.stars ? "#e7a934" : "#e4e0d8"}}
                                />
                              ))}
                            </div>

                            {r.text && <p className={styles.reviewText}>{r.text}</p>}
                            {r.images?.length > 0 && (
                              <div className={styles.reviewCardPhotos}>
                                {r.images.map((img, i) => (
                                  <button key={img.id} type="button" onClick={() => openLightbox(r.images, i)}>
                                    <img src={mediaUrl(img.imageUrl)} alt="صورة من العميل" loading="lazy" />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}
            </>
          ) : (
            <p className="warn">لا يوجد منتج بهذه المواصفات حاليا</p>
          )}

          {/* Full-size photo viewer (review photos, FAQ images) */}
          {lightbox && (
            <div
              className={styles.lightbox}
              role="dialog"
              aria-label="صور التقييم"
              onClick={(e) => e.target === e.currentTarget && setLightbox(null)}
            >
              <button type="button" className={styles.lightboxClose} onClick={() => setLightbox(null)} aria-label="إغلاق">
                <FiX />
              </button>
              {lightbox.images.length > 1 && (
                <>
                  <button
                    type="button"
                    className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                    onClick={() => setLightbox((l) => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }))}
                    aria-label="السابق"
                  >
                    <FiChevronRight />
                  </button>
                  <button
                    type="button"
                    className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                    onClick={() => setLightbox((l) => ({ ...l, index: (l.index + 1) % l.images.length }))}
                    aria-label="التالي"
                  >
                    <FiChevronLeft />
                  </button>
                </>
              )}
              <img src={lightbox.images[lightbox.index]} alt="صورة من العميل" className={styles.lightboxImg} />
            </div>
          )}

          {/* -------- Trust feature bar -------- */}
          <section className={styles.section}>
            <p className={styles.slogan}>جودة فنية تُلهِم الحواس وتلامس المشاعر</p>

            <div className={styles.featureBar}>
              <div className={styles.featureItem}>
                <MdVerifiedUser />
                <span>شراء آمن وهادئ<br />نظام حماية مُعتمد وفق معيار عالمي</span>
              </div>
              <div className={styles.featureItem}>
                <FaAward />
                <span>رضاكم مضمون<br />ضمان كامل على كل لوحة نُقدّمها</span>
              </div>
              <div className={styles.featureItem}>
                <FaHandshake />
                <span>نحن هنا لأجلكم<br />متابعة شخصية وخدمة راقية بلا مثيل</span>
              </div>
              <div className={styles.featureItem}>
                <FaShippingFast />
                <span>حتى باب بيتكم<br />شحن سريع وتغليف مُتقن بمحبة</span>
              </div>
            </div>
          </section>

          {/* -------- FAQ -------- */}
          <section className={styles.section}>
            <div className={styles.faqBox}>
              <h2 className={styles.sectionTitle}>اعرف اكثر عن لوحاتنا الفنية</h2>

              {faqs.map((faq, idx) => (
                <div key={faq.id ?? idx} className={styles.faqItem}>
                  <button
                    type="button"
                    className={styles.faqQuestion}
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    aria-expanded={openFaq === idx}
                  >
                    {faq.question}
                    <FiChevronDown className={openFaq === idx ? styles.faqChevronOpen : styles.faqChevron} />
                  </button>

                  {openFaq === idx && (
                    <div className={styles.faqAnswerWrap}>
                      <p className={styles.faqAnswer}>{faq.answer}</p>
                      {faq.imageUrl && (
                        <img
                          src={mediaUrl(faq.imageUrl)}
                          alt={faq.question}
                          loading="lazy"
                          className={styles.faqImage}
                          onClick={() => setLightbox({ images: [mediaUrl(faq.imageUrl)], index: 0 })}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* -------- About -------- */}
          <section className={styles.section}>
            <div className={styles.aboutBox}>
              <h2 className={styles.aboutTitle}>فَـــذَكِّر إِنْ نَفَعَت الذِكْرَى</h2>

              <p className={styles.aboutIntro}>في عالم يتجدد فيه الإبداع وتتكامل فيه الفنون مع التقنيات الحديثة، انطلقت علامة فَذَكِّرْ في عام 2024، لتكون رحلة فنية متكاملة تُعيد الحياة إلى الجدران وتُنيرها بقيمنا الإسلامية الأصيلة.</p>

              <button className={styles.aboutMore} onClick={() => showAbout ? setShowAbout(false) : setShowAbout(true)}>
                {showAbout ? "عرض اقل" : "عرض المزيد"}
              </button>

              {showAbout &&
                <div className="aboutus">
                  <h3>لماذا تختار فَذَكِّرْ ؟</h3>
                  <p className="title">فن متقن وتصميم فريد</p>
                  <p>فريقنا من المصممين المبدعين يضم خطاطين مهرة يعملون على تصميم كل لوحة من الصفر وفقًا لذوقك الخاص. نستخدم الخط العربي الراقي ليُضفي رونقًا خاصًا ومميزًا يُبرز جمال الآيات القرآنية ويُذكرنا دائماً بإيماننا.</p>
                  <p className="title">حلول مبتكرة لتجديد المساحات</p>
                  <p>نحن نعلم أن الحائط الفارغ يُشعر بالبرود واللامبالاة، ولهذا نقدم لك فرصة تحويل كل جدار إلى لوحة فنية تنبض بالحياة. أعمالنا الفنية ليست مجرد زينة، بل هي رسالة تحمل قيم الإسلام الأصيلة؛ "القيم الإسلامية على جدرانك" و"لا مزيد من الجدران الفارغة".</p>
                  <p className="title">تجربة مميزة قبل الشراء</p>
                  <p>لن تشتري قطعة فنية عادية، بل سنوفر لك خدمة الصورة الافتراضية التي تُظهر كيف ستبدو اللوحة في ديكور منزلك قبل اتخاذ القرار النهائي، لضمان توافق الألوان والحجم مع المساحة الخاصة بك.</p>
                  <p className="title">خدمة صمم لوحتك </p>
                  <p>ولأننا ندرك أن لكل مساحة طابعها الخاص، نقدم لك خدمة "صمم لوحتك"، حيث يمكنك طلب تصميم فريد من الصفر بناءً على رؤيتك الخاصة، مع اختيار الألوان والخلفيات التي تناسب ذوقك. كما نتيح لك إمكانية تفصيل اللوحات بأي حجم تحتاجه، سواء كان صغيرًا يزين زاوية خاصة أو كبيرًا يغطي جدارًا كاملاً ليكون تحفة فنية مميزة في منزلك أو مكتبك.</p>
                  <p className="title">جودة استثنائية</p>
                  <p>نستخدم في طباعة الكانفاس النوعية المستخدمة في المتاحف ، مما يضمن لك جودة تدوم 25 سنة بدون أي مسح أو تنظيف، لتظل أعمالنا شاهدة على الإبداع والتميز.</p>
                  <p className="title">تأثير إيجابي على ضيوفك</p>
                  <p>دع ديكور منزلك يحكي قصة إيمانك وروحانيتك، فعملنا الفني سيبهر ضيوفك ويحول كل زاوية إلى قطعة من لوحة فنية تنبض بالأصالة والحداثة.</p>
                  <p className="title">التغليف والاهتمام بالتفاصيل</p>
                  <p>نحن ندرك أهمية أن تصل لوحتك إليك بحالة مثالية، لذلك نولي اهتمامًا كبيرًا بعملية التغليف. يتم استخدام مواد عالية الجودة تضمن حماية اللوحة من أي ضرر أثناء الشحن، سواء كانت خدوشًا أو كسورًا. نحرص على أن تبقى اللوحة بأبهى حلتها لتزين جدران منزلك أو مكتبك بكل فخر.</p>
                  <p className="title">هدية لطيفة مع كل لوحة</p>
                  <p>ولأننا نؤمن بأن التفاصيل الصغيرة تصنع الفرق، فإننا نرفق مع كل لوحة هدية لطيفة تترك بصمة دافئة في قلبك، وتجعل اسم "فَذَكِّرْ" حاضرًا في ذهنك دائمًا. نسعى من خلال هذه اللمسة البسيطة إلى أن تكون تجربتك معنا مليئة بالذكرى الطيبة والرضا.</p>
                  <p className="title">رسالتنا ودعوتنا لك</p>
                  <p>"امنح حائطك الحياة، وحوّله إلى رسالة تذكرنا دائمًا بإيماننا وقيمنا الإسلامية. اطلب تصميمك المخصص الآن واستمتع بتجربة فريدة تُضفي على منزلك رونقًا خاصًا وجاذبية لا تُضاهى."</p>
                </div>
              }
            </div>
          </section>

          {/* -------- Related products -------- */}
          {relatedError && (
            <p className="warn">حدث خطأ اثناء تحميل المنتجات. حاول مرة اخرى</p>
          )}

          {isLoadingRelated ? (
            <RelatedSkeleton count={2} />
          ) : (
            <>
              {relatedProducts.length > 0 ? (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle}>اكتشف المزيد</h2>

                  <div className={styles.relatedSlider}>
                    <Swiper
                      modules={[Navigation]}
                      slidesPerView={1.6}
                      spaceBetween={18}
                      loop={true}
                      navigation={{enabled: false}}
                      breakpoints={{
                        712: {
                          slidesPerView: 2.5
                        },
                        1024: {
                          slidesPerView: 3.5,
                        },
                        1200: {
                          slidesPerView: 4,
                          navigation: {enabled: true}
                        }
                      }}
                      touchEventsTarget="container"
                      allowTouchMove={true}
                      simulateTouch={true}
                      resistance={true}
                    >
                      {relatedProducts.map((product) => {
                        const lowestPrice = lowestPrices?.find(p => p.product_id === product.product_id)?.min_price;
                        return (
                          <SwiperSlide key={product.product_id}>
                            <div
                              className={styles.relatedCard}
                              onClick={() => navigate(`/product/${product.product_id}`)}
                            >
                              <div className={styles.relatedImg}>
                                <ProductImage
                                  url={product.image_url}
                                  alt={product.name}
                                  loading="lazy"
                                />
                                <button
                                  type="button"
                                  className={styles.relatedWish}
                                  aria-label="أضف للمفضلة"
                                  onClick={(e) => toggleRelatedWish(e, product, lowestPrice)}
                                >
                                  <FiHeart className={isRelatedWished(product) ? styles.relatedWished : ""} />
                                </button>
                              </div>
                              <div className={styles.relatedBody}>
                                <p className={styles.relatedName}>{product.name}</p>
                                <div className={styles.relatedPrice}>يبدأ من {lowestPrice} ₪</div>

                                <div className={styles.relatedActions}>
                                  <button
                                    type="button"
                                    className={styles.relatedBuy}
                                    onClick={(e) => relatedBuyNow(e, product)}
                                    disabled={!!relatedBusy}
                                  >
                                    {relatedBusy === `${product.product_id}:buy`
                                      ? <ImSpinner2 className={styles.relatedSpin} />
                                      : <><BsLightningChargeFill /> اشترِ الآن</>}
                                  </button>
                                  <button
                                    type="button"
                                    className={styles.relatedCart}
                                    onClick={(e) => relatedAddToCart(e, product)}
                                    disabled={!!relatedBusy}
                                    aria-label="أضف إلى السلة"
                                    title="أضف إلى السلة"
                                  >
                                    {relatedBusy === `${product.product_id}:cart`
                                      ? <ImSpinner2 className={styles.relatedSpin} />
                                      : <BsCartCheck />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </SwiperSlide>
                        )
                      })}
                    </Swiper>
                  </div>

                  <button
                    type="button"
                    className={styles.showMoreRelated}
                    onClick={() => navigate(`/products?category=${product.categoryIds[0]}`)}
                  >
                    عرض المزيد من هذه الفئة
                  </button>
                </section>
              ) : (
                <button className='explore center' onClick={() => navigate("/products")}>
                  <img src={explore} alt="explore" />
                  <p>اكتشف المزيد من المنتجات</p>
                </button>
              )}
            </>
          )}
        </>
      )}

      <SpecialOrderModal isOpen={openSpecial} onClose={() => setOpenSpecial(false)} />
    </div>
  );
};

export default ProductPage;
